/* eslint-disable no-underscore-dangle */
/* eslint-disable react/jsx-props-no-spreading */
import { Button, Form, Modal } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import React, { useState } from 'react';
import { defaults } from 'lodash';
import setStateFromInput from '../../../Utils/setStateFromInput';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import Error from '../../../Components/Error';
import BookingService from '../../../Services/BookingService';

// Billingo's own series, e.g. 2026-000627. Anything else is asked about, not
// refused: some invoices come from external issuers.
const BILLINGO_NUMBER = /^\d{4}-\d{6}$/;

function AddInvoiceModal({
  originalOrder = {}, show = false, handleClose,
}) {
  const defaultOrder = {
    email: originalOrder.email_address,
    file: null,
  };

  const [order, setOrder] = useState(defaults(originalOrder, defaultOrder));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [fileState, setFileState] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const handleChange = (e) => {
    setStateFromInput(e, setOrder);
  };

  const handleDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setFileState(file);

      const reader = new FileReader();
      reader.onload = () => {
        const base64File = reader.result.split(',')[1]; // Extract Base64 data
        setOrder((prevOrder) => ({
          ...prevOrder,
          file: base64File, // Save the Base64 string to state
        }));
      };
      reader.readAsDataURL(file); // Read file as Base64
    }
  };

  const handleRemoveFile = () => {
    setFileState(null);
    setOrder((prevOrder) => ({
      ...prevOrder,
      file: null,
    }));
  };

  const handleSubmit = async () => {
    const number = invoiceNumber.trim();
    // eslint-disable-next-line no-alert
    if (!BILLINGO_NUMBER.test(number) && !window.confirm(
      `"${number}" doesn't look like a Billingo invoice number (YYYY-NNNNNN, e.g. 2026-000627). Upload it anyway?`,
    )) return;

    setLoading(true);

    try {
      const data = {
        _id: order._id,
        email: order.email,
        file: order.file,
        invoice_number: number,
      };
      await BookingService.addInvoice(data);
      handleClose();
    } catch (err) {
      setError(err.response.data?.message);

      if (err.response.data.errors) {
        setValidationErrors(err.response.data?.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: '.pdf',
    multiple: false,
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / (k ** i)) * 100) / 100} ${sizes[i]}`;
  };

  return (
    <Modal show={show} onHide={handleClose} size="md">
      <Modal.Header closeButton>
        <Modal.Title>Add Invoice</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <div className="row mb-4">
          <Form.Group className="col-12" controlId="email">
            <Form.Label>Email(s)</Form.Label>
            <Form.Control
              value={order.email}
              placeholder="customer@gmail.com,customer2@gmail.com"
              onChange={(e) => handleChange(e)}
            />
            <FormControlFeedback errors={validationErrors} field="email" />
          </Form.Group>
        </div>

        <div className="row mb-4">
          <Form.Group className="col-12" controlId="invoice_number">
            <Form.Label>Invoice number</Form.Label>
            <Form.Control
              required
              value={invoiceNumber}
              placeholder="2026-000627"
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
            <FormControlFeedback errors={validationErrors} field="invoice_number" />
          </Form.Group>
        </div>

        <Form.Group>
          <Form.Label>Invoice File</Form.Label>
          <div
            {...getRootProps()}
            style={{
              border: `2px dashed ${isDragActive ? '#0d6efd' : '#ccc'}`,
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
              transition: 'all 0.2s ease',
            }}
          >
            <input {...getInputProps()} />
            <div>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ margin: '0 auto 16px', color: '#6c757d' }}
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              {isDragActive ? (
                <p style={{ margin: '0 0 8px', fontWeight: '500' }}>
                  Drop the file here...
                </p>
              ) : (
                <>
                  <p style={{ margin: '0 0 8px', fontWeight: '500' }}>
                    Drag & drop invoice file here, or click to select
                  </p>
                  <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
                    Only PDF files are accepted (max 10MB)
                  </p>
                </>
              )}
            </div>
          </div>

          {fileState && (
            <div className="mt-3">
              <ul className="list-group">
                <li className="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{fileState.name}</strong>
                    <small className="text-muted ms-2">
                      (
                      {formatFileSize(fileState.size)}
                      )
                    </small>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleRemoveFile}
                  >
                    Remove
                  </Button>
                </li>
              </ul>
            </div>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleSubmit} disabled={loading || !order.file || !invoiceNumber.trim()}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Uploading...
            </>
          ) : (
            'Add Invoice'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddInvoiceModal;
